import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CJMSClient, cjmsApi, CommunicationEntry } from '../api/cjmsApi';
import { useCjmsStore } from '../store/useCjmsStore';
import { getStageContent } from '../api/stageContent';

interface IntelligencePanelProps {
    client: CJMSClient;
}

type PanelState = 'idle' | 'loading' | 'ready' | 'error';

export const IntelligencePanel: React.FC<IntelligencePanelProps> = ({ client }) => {
    const [script, setScript] = useState<string>('');
    const [state, setState] = useState<PanelState>('idle');
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [copied, setCopied] = useState(false);
    const [showFallbackScripts, setShowFallbackScripts] = useState(false);
    const [assessmentData, setAssessmentData] = useState<any>(null);

    const isLead = client.source === 'lead';
    const stageContent = getStageContent(client.currentStage, isLead);

    // Fetch assessment data if available
    useEffect(() => {
        if (client.email) {
            cjmsApi.getAssessmentResults(client.email)
                .then(result => {
                    if (result.success && result.assessment) {
                        setAssessmentData(result.assessment);
                    }
                })
                .catch(() => {});
        }
    }, [client.email]);

    const fetchScript = useCallback(async () => {
        setScript('');
        setErrorMsg('');
        setState('loading');
        try {
            const result = await cjmsApi.fetchSuggestedScript(client.email);
            if (result && !/not found|unavailable/i.test(result)) {
                setScript(result);
                setState('ready');
            } else {
                setErrorMsg(result || 'AI did not return a script.');
                setState(result && /not found/i.test(result) ? 'idle' : 'error');
            }
        } catch (err: any) {
            setErrorMsg(err?.message || 'Unexpected error');
            setState('error');
        }
    }, [client.email]);

    useEffect(() => {
        fetchScript();
    }, [fetchScript]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(script);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard may be unavailable
        }
    };

    const handleRegenerate = async () => {
        await fetchScript();
    };

    const handleSelectFallback = (fallbackScript: string) => {
        const filled = fallbackScript.replace(/\{\{name\}\}/g, client.name);
        setScript(filled);
        setState('ready');
        setShowFallbackScripts(false);
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-sm font-bold">
                        ✦
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-slate-800 leading-tight">Intelligence Hub</h3>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">AI-suggested next-step message</p>
                    </div>
                </div>
                <button
                    onClick={handleRegenerate}
                    disabled={state === 'loading'}
                    className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
                >
                    {state === 'loading' ? (
                        <>
                            <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            Generating…
                        </>
                    ) : 'Regenerate'}
                </button>
            </div>

            <AnimatePresence mode="wait">
                {state === 'loading' && !script && (
                    <motion.div
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="p-8 text-center border-2 border-dashed border-slate-200 rounded-lg"
                    >
                        <div className="inline-block w-6 h-6 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin mb-2" />
                        <p className="text-sm text-slate-500">
                            {client.lastCommunicationContent
                                ? 'Crafting a response based on your last WhatsApp interaction…'
                                : `Crafting a stage-appropriate response for ${client.name}…`}
                        </p>
                    </motion.div>
                )}

                {state === 'ready' && script && (
                    <motion.div
                        key="ready"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 bg-gradient-to-br from-indigo-50 to-white rounded-lg border border-indigo-100 relative group"
                    >
                        <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <span>WhatsApp-ready Script</span>
                            <span className="px-1.5 py-0.5 bg-indigo-100 rounded text-[9px]">{client.currentStage}</span>
                        </div>
                        <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                            "{script}"
                        </p>
                        <div className="flex gap-2 mt-3 opacity-60 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={handleCopy}
                                className="px-2 py-1 text-[10px] font-medium text-indigo-700 bg-white border border-indigo-200 rounded hover:bg-indigo-50 transition-colors"
                            >
                                {copied ? 'Copied ✓' : '📋 Copy'}
                            </button>
                        </div>
                    </motion.div>
                )}

                {(state === 'idle' || state === 'error') && (
                    <motion.div
                        key={state}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="p-4 text-center border-2 border-dashed rounded-lg"
                    >
                        <p className="text-sm text-slate-500 mb-3">
                            {state === 'error'
                                ? `${errorMsg} — but here are stage-specific scripts you can use:`
                                : `AI generation unavailable. Choose from ${stageContent.displayName} scripts below:`}
                        </p>

                        {stageContent.scripts.length > 0 && (
                            <div className="space-y-2 mt-2">
                                {stageContent.scripts.slice(0, 4).map((fallbackScript, i) => (
                                    <motion.button
                                        key={i}
                                        whileHover={{ scale: 1.01 }}
                                        onClick={() => handleSelectFallback(fallbackScript)}
                                        className="w-full text-left p-3 bg-white border border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-all text-xs text-slate-700"
                                    >
                                        <span className="text-indigo-400 font-bold mr-1">#{i + 1}</span>
                                        {fallbackScript.replace(/\{\{name\}\}/g, client.name)}
                                    </motion.button>
                                ))}
                            </div>
                        )}

                        {stageContent.tasks.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-slate-100">
                                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                    Suggested Next Actions
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {stageContent.tasks.slice(0, 4).map((task, i) => (
                                        <span key={i} className="px-2 py-1 bg-amber-50 border border-amber-100 rounded text-[10px] text-amber-800">
                                            {typeof task === 'string' ? task : task.label || JSON.stringify(task)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Communication context that feeds this Intelligence Hub */}
            {client.lastCommunicationContent && (
                <div className="mt-4 p-3 bg-green-50 border border-green-100 rounded-lg">
                    <div className="flex items-center gap-1 mb-1">
                        <span className="text-[10px]">💬</span>
                        <span className="text-[10px] font-semibold text-green-700 uppercase tracking-wider">
                            Last WhatsApp from {client.name}
                        </span>
                        <span className="text-[9px] text-green-500 ml-auto">{client.lastCommunication}</span>
                    </div>
                    <p className="text-xs text-green-900 italic">
                        "{client.lastCommunicationContent}"
                    </p>
                    {client.lastCommunicationChannel && (
                        <p className="text-[10px] text-green-600 mt-1">
                            via {client.lastCommunicationChannel}
                        </p>
                    )}
                </div>
            )}

            {/* Freedom Number & Assessment Insights */}
            {assessmentData && (
                <div className="mt-4 p-3 bg-gradient-to-br from-indigo-50 to-white rounded-lg border border-indigo-100">
                    <div className="flex items-center gap-1 mb-2">
                        <span className="text-[10px]">🎯</span>
                        <span className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wider">
                            Freedom Number Analysis
                        </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="p-2 bg-white rounded border border-indigo-50">
                            <span className="text-indigo-500 text-[10px]">Current Revenue</span>
                            <p className="font-bold text-indigo-800">R{(assessmentData.currentState?.revenue || 0).toLocaleString()}/mo</p>
                        </div>
                        <div className="p-2 bg-white rounded border border-indigo-50">
                            <span className="text-indigo-500 text-[10px]">Freedom Number</span>
                            <p className="font-bold text-green-700">R{(assessmentData.freedomNumber?.target || 0).toLocaleString()}/mo</p>
                        </div>
                        <div className="p-2 bg-white rounded border border-indigo-50">
                            <span className="text-indigo-500 text-[10px]">Revenue Gap</span>
                            <p className="font-bold text-amber-600">R{(assessmentData.freedomNumber?.gap || 0).toLocaleString()}/mo</p>
                        </div>
                        <div className="p-2 bg-white rounded border border-indigo-50">
                            <span className="text-indigo-500 text-[10px]">Customers Needed</span>
                            <p className="font-bold text-indigo-800">{assessmentData.freedomNumber?.additionalCustomersNeeded || 0}</p>
                        </div>
                    </div>

                    {assessmentData.recommendations && assessmentData.recommendations.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-indigo-100">
                            <span className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">Top Recommendation</span>
                            <p className="text-xs text-indigo-800 mt-1">{assessmentData.recommendations[0]}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Recent communication timeline context for the Hub */}
            <CommStripContext client={client} />
        </div>
    );
};

// Lightweight preview of the last few Communications entries to give the Hub richer context.
const CommStripContext: React.FC<{ client: CJMSClient }> = ({ client }) => {
    const [recent, setRecent] = useState<CommunicationEntry[]>([]);
    const storeActions = useCjmsStore(state => state.actions);

    useEffect(() => {
        let cancelled = false;
        storeActions.loadCommunicationHistory(client.email, client.phone, client.id.replace(/^(customer_|lead_)/, ''))
            .then(entries => {
                if (cancelled) {return;}
                setRecent(entries.slice(0, 3));
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [client.email, client.phone]);

    if (recent.length === 0) {return null;}

    return (
        <div className="mt-3 p-3 bg-slate-50 border border-slate-100 rounded-lg">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Recent conversation context
            </div>
            <div className="space-y-1.5">
                {recent.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                        <span className={`shrink-0 font-bold ${r.direction === 'outgoing' ? 'text-indigo-600' : 'text-green-600'}`}>
                            {r.direction === 'outgoing' ? '→' : '←'}
                        </span>
                        <span className="text-slate-600 truncate">{r.content || <span className="italic text-slate-400">no text</span>}</span>
                        <span className="ml-auto text-[10px] text-slate-400 shrink-0">{r.date}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default IntelligencePanel;
